import { FlowData, FlowElementTypes } from '@sdk';
import { useMemo, useRef } from 'react';

import { transcriptEntry } from './transcriptEntry';

/**
 * Groups a flat FlowData stream into a sequence of "turn groups" suitable for
 * the dense floating-chat layout: text-shaped messages stay as-is, and any
 * contiguous run of tool/status/error events between two messages collapses
 * into a single dense group that the UI renders as one row with an expand
 * toggle.
 *
 * A contiguous run of REASONING frames collapses the same way into its own
 * collapsible `thinking` row (`ThinkingSummary`), independent of the
 * dense group's "show tool calls" gate.
 *
 * Why this lives client-side: the worker drivers (Claude / Codex
 * `event_to_flowdata.py`) emit each event individually with no notion of
 * "turns" — turn boundaries are just where the assistant text resumes.
 */

export type TurnGroup =
  | {
      kind: 'message';
      flowData: FlowData;
      index: number;
      /**
       * Set on the framework-injected (`is-meta`) message that follows a
       * dropped `Skill` tool call: the skill's name, lifted off that call's
       * structured payload. The chip prefers it over regexing the injected
       * body — see `MetaMessageChip`.
       */
      skillName?: string;
    }
  | { kind: 'worker-unavailable'; flowData: FlowData; index: number }
  | { kind: 'dense'; events: FlowData[]; index: number }
  /** Every REASONING frame between two messages, collected into its own collapsible row (`ThinkingSummary`), separate from `dense` and its "show tool calls" gate. */
  | { kind: 'thinking'; events: FlowData[]; index: number };

const MESSAGE_TYPES = new Set<string>([
  FlowElementTypes.USER_MESSAGE,
  FlowElementTypes.CHAT,
  FlowElementTypes.TEXT,
]);

const DENSE_TYPES = new Set<string>([
  FlowElementTypes.TOOL_CALL,
  FlowElementTypes.TOOL_RESULT,
  FlowElementTypes.STATUS,
  FlowElementTypes.ERROR,
]);

/**
 * Transcript bookkeeping that is NOT "something the agent did": per-snapshot
 * token accounting and Claude's session-envelope lines (file-history snapshots,
 * queue operations, titles). A replayed transcript carries dozens of them, and
 * they buried the real operations under a wall of empty `{}` rows. Live
 * lifecycle STATUS frames (`init`, `task_started`, `rate-limit`) are NOT here —
 * those describe the run and stay visible.
 */
const NON_ACTIVITY_SUBTYPES = new Set<string>(['token_usage', 'meta', 'summary']);

const NO_LIVE_EVENTS: FlowData[] = [];

/**
 * Split the trailing "in-flight" dense group off a grouped turn stream: while
 * `active`, its still-running tool/status/error events are the last dense
 * group, split out so a chat surface can show it separately (e.g. a footer chip).
 *
 * A live `thinking` group is NOT split off this way — it stays inline,
 * growing in place; see `ThinkingSummary`.
 */
export function splitLiveGroup(
  groups: TurnGroup[],
  active: boolean,
): { inlineGroups: TurnGroup[]; liveEvents: FlowData[] } {
  const last = active ? groups[groups.length - 1] : undefined;
  if (last?.kind === 'dense') {
    return { inlineGroups: groups.slice(0, -1), liveEvents: last.events };
  }
  return { inlineGroups: groups, liveEvents: NO_LIVE_EVENTS };
}

export function groupTurnEvents(items: readonly FlowData[]): TurnGroup[] {
  return createTurnGrouper().next(items);
}

/**
 * Incremental, identity-stable turn grouping.
 *
 * A live stream appends one frame at a time, and every appended frame used to
 * re-run `groupTurnEvents` over the WHOLE array AND hand every consumer brand
 * new group objects — so React re-rendered every turn row on every frame
 * (QA issue D10: 1,000+ frame histories made each frame an O(n) regroup plus
 * a full-list re-render). The grouper keeps the last input/output: when the
 * new items array is an append-only extension of the previous one (checked by
 * cheap reference-equality over the shared prefix), only the appended items
 * are consumed and every COMMITTED group keeps its object identity — so
 * memoized rows skip re-rendering. Only the trailing dense group (still
 * growing until the next message arrives) gets a fresh identity per call.
 *
 * Any non-append change (clear + force reload, truncation, in-place
 * replacement) falls back to a full rebuild — correctness never depends on
 * the append-only fast path.
 */
export interface TurnGrouper {
  next(items: readonly FlowData[]): TurnGroup[];
}

export function createTurnGrouper(): TurnGrouper {
  let prevItems: readonly FlowData[] = [];
  let lastOutput: TurnGroup[] = [];
  /** Frozen groups: message groups, and dense groups sealed by a later message. */
  let committed: TurnGroup[] = [];
  /** Trailing dense run, not yet sealed — rebuilt with fresh identity per call. */
  let tail: FlowData[] = [];
  /** Trailing reasoning run, not yet sealed — parallels `tail` but for REASONING
   *  frames, so a thinking block never merges into the tool/status chip. */
  let thinkingTail: FlowData[] = [];
  let skillCallIds = new Set<string>();
  /** Name from the most recent dropped Skill call, awaiting its meta message. */
  let pendingSkillName: string | null = null;
  /** Emitted dense rows by transcript-entry id — the handles `retract` needs. */
  let emittedByEntryId = new Map<string, FlowData>();
  /** tool_use_ids still claimed by a rendered TOOL_CALL, and ids whose only
   *  call(s) were dropped. A result is dropped only when the id is in the
   *  second set and NOT in the first — a refinement inherits its source's
   *  tool_use_id, so the leaf keeps claiming it. */
  let liveCallIds = new Set<string>();
  let droppedCallIds = new Set<string>();

  const reset = () => {
    committed = [];
    tail = [];
    thinkingTail = [];
    skillCallIds = new Set<string>();
    pendingSkillName = null;
    emittedByEntryId = new Map<string, FlowData>();
    liveCallIds = new Set<string>();
    droppedCallIds = new Set<string>();
  };

  /** Un-render an already-emitted row: its refinement just arrived. */
  const retract = (sourceEntryId: string) => {
    const item = emittedByEntryId.get(sourceEntryId);
    if (!item) return; // never rendered — dropped earlier, or not a dense row
    emittedByEntryId.delete(sourceEntryId);
    const toolUseId = getToolUseId(item);
    if (toolUseId) {
      liveCallIds.delete(toolUseId);
      droppedCallIds.add(toolUseId);
    }
    const inTail = tail.lastIndexOf(item);
    if (inTail >= 0) {
      tail.splice(inTail, 1);
      return;
    }
    // Already sealed by an intervening message. Rebuild that group with a fresh
    // identity so memoized rows notice; adjacency is the normal case, not a
    // guarantee this module may lean on.
    for (let i = committed.length - 1; i >= 0; i--) {
      const group = committed[i];
      if (group.kind !== 'dense') continue;
      const at = group.events.lastIndexOf(item);
      if (at < 0) continue;
      const events = [...group.events];
      events.splice(at, 1);
      committed[i] = { ...group, events };
      return;
    }
  };

  const flushTail = () => {
    if (tail.length === 0) return;
    committed.push({ kind: 'dense', events: tail, index: committed.length });
    tail = [];
  };

  /** Seal the reasoning run collected since the last message, if any. Kept
   *  distinct from `flushTail` so a caller could flush one without the
   *  other, though every current call site flushes both together. */
  const flushThinkingTail = () => {
    if (thinkingTail.length === 0) return;
    committed.push({ kind: 'thinking', events: thinkingTail, index: committed.length });
    thinkingTail = [];
  };

  const consume = (item: FlowData) => {
    const t: string = item.elementType;
    if (MESSAGE_TYPES.has(t)) {
      // Thinking precedes the tool/status summary, which precedes the
      // message it explains — "the agent thought, then acted, then replied".
      flushThinkingTail();
      flushTail();
      const isMeta = item.attributes['is-meta'] === 'true';
      const skillName = isMeta && pendingSkillName ? pendingSkillName : undefined;
      if (isMeta) pendingSkillName = null;
      committed.push({ kind: 'message', flowData: item, index: committed.length, skillName });
    } else if (t === FlowElementTypes.WORKER_UNAVAILABLE) {
      flushThinkingTail();
      flushTail();
      committed.push({ kind: 'worker-unavailable', flowData: item, index: committed.length });
    } else if (t === FlowElementTypes.REASONING) {
      // Collected separately from DENSE_TYPES so a thinking summary never
      // merges into the generic tool/status chip — see the `thinking` TurnGroup.
      thinkingTail.push(item);
    } else if (DENSE_TYPES.has(t)) {
      if (NON_ACTIVITY_SUBTYPES.has(item.attributes['subtype'])) return;
      // A skill invocation is already represented in the chat by the skill's
      // meta-injection chip (MetaMessageChip, "Using skill: <name>") — the
      // injected body arrives as an isMeta USER_MESSAGE right after the call.
      // Keeping the TOOL_CALL/TOOL_RESULT in the dense stream too rendered
      // duplicate "Using Skill" chips around that one, so drop the pair here.
      if (isSkillToolEvent(item, skillCallIds)) {
        const name = skillNameOf(item);
        if (name) pendingSkillName = name;
        const toolUseId = getToolUseId(item);
        if (toolUseId) droppedCallIds.add(toolUseId);
        return;
      }
      // Derivation is ADDITIVE: this row may be the refinement of one already
      // rendered. Suppression is generic — an entry that is the `derived_from`
      // of an entry present in the stream never renders — so a chain
      // (shell_command → flow_command → artifact) collapses to its leaf with no
      // per-kind rules here.
      const source = derivedFromOf(item);
      if (source) retract(source);

      const toolUseId = getToolUseId(item);
      if (t === FlowElementTypes.TOOL_RESULT) {
        // Drop only a genuinely orphaned result: its call was suppressed AND no
        // survivor claims the id. A refinement INHERITS its source's
        // tool_use_id, so the common case is that the leaf still claims it.
        if (toolUseId && droppedCallIds.has(toolUseId) && !liveCallIds.has(toolUseId)) return;
      } else if (t === FlowElementTypes.TOOL_CALL && toolUseId) {
        liveCallIds.add(toolUseId);
      }
      const entryId = transcriptEntryIdOf(item);
      if (entryId) emittedByEntryId.set(entryId, item);
      tail.push(item);
    }
    // Anything else (END, RESULT, CHECKPOINT, …) is intentionally dropped from
    // the visible chat — the dense surface is for "things the agent did", not
    // every internal stream marker.
  };

  const isAppendOnlyExtension = (items: readonly FlowData[]): boolean => {
    if (items.length < prevItems.length) return false;
    for (let i = 0; i < prevItems.length; i++) {
      if (items[i] !== prevItems[i]) return false;
    }
    return true;
  };

  return {
    next(items: readonly FlowData[]): TurnGroup[] {
      let start: number;
      if (isAppendOnlyExtension(items)) {
        if (items.length === prevItems.length) return lastOutput;
        start = prevItems.length;
      } else {
        reset();
        start = 0;
      }
      for (let i = start; i < items.length; i++) consume(items[i]);
      prevItems = items;
      // Mirrors commit order: a still-open thinking run renders before the
      // still-open tool/status run, both after the last committed group.
      const trailing: TurnGroup[] = [];
      if (thinkingTail.length > 0) {
        trailing.push({ kind: 'thinking', events: [...thinkingTail], index: committed.length + trailing.length });
      }
      if (tail.length > 0) {
        trailing.push({ kind: 'dense', events: [...tail], index: committed.length + trailing.length });
      }
      lastOutput = trailing.length > 0 ? [...committed, ...trailing] : [...committed];
      return lastOutput;
    },
  };
}

/**
 * React binding for {@link createTurnGrouper}: one grouper per component
 * instance, re-grouping (incrementally) only when the items snapshot changes.
 * Committed groups keep referential identity across appends, so memoized row
 * components skip re-rendering on every live frame.
 */
export function useTurnGroups(items: FlowData[]): TurnGroup[] {
  const grouperRef = useRef<TurnGrouper | null>(null);
  if (grouperRef.current === null) grouperRef.current = createTurnGrouper();
  return useMemo(() => grouperRef.current!.next(items), [items]);
}

/** This frame's transcript-entry id; null for frames that carry none. */
function transcriptEntryIdOf(item: FlowData): string | null {
  const id = transcriptEntry(item)?.id ?? item.attributes['transcript-entry-id'];
  return typeof id === 'string' && id ? id : null;
}

/** The id of the entry this one REFINES, or null when it is physical. */
function derivedFromOf(item: FlowData): string | null {
  const from = transcriptEntry(item)?.derived_from;
  return typeof from === 'string' && from ? from : null;
}

/** The transcript kind behind a frame (`shell_command`, `skill_call`, …). */
function entryKindOf(item: FlowData): string {
  const kind = transcriptEntry(item)?.kind ?? item.attributes['subtype'];
  return typeof kind === 'string' ? kind : '';
}

/** The worker-side tool name Claude uses to load a skill. */
const SKILL_TOOL_NAME = 'Skill';

/**
 * Is this frame a skill invocation?
 *
 * Deliberately NOT `tool-name === 'Skill'` alone: codex has no Skill tool — it
 * loads a skill by reading its SKILL.md, so its `SkillCallEntry` carries
 * `tool-name: shell`. Matching only the Claude name is why codex rendered two
 * chips for one skill load. The authority is the transcript KIND, with the
 * `skill-name` attribute (stamped by every `SkillCallEntry`) and Claude's tool
 * name as fallbacks for frames that reach us without a `process_entry`.
 */
function isSkillCallFrame(item: FlowData): boolean {
  return (
    entryKindOf(item) === 'skill_call' ||
    !!item.attributes['skill-name'] ||
    item.attributes['tool-name'] === SKILL_TOOL_NAME
  );
}

function isSkillToolEvent(item: FlowData, skillCallIds: Set<string>): boolean {
  if (item.elementType === FlowElementTypes.TOOL_CALL && isSkillCallFrame(item)) {
    const id = getToolUseId(item);
    if (id) skillCallIds.add(id);
    return true;
  }
  if (item.elementType === FlowElementTypes.TOOL_RESULT) {
    const id = getToolUseId(item);
    return !!id && skillCallIds.has(id);
  }
  return false;
}

/**
 * The skill a `Skill` TOOL_CALL loaded, from the backend's structured payload.
 * Both the live stream and a replay carry it (`skill-name` attribute /
 * `skill_name` in the flow value), so the chip never has to parse prose.
 */
function skillNameOf(item: FlowData): string | null {
  const attr = item.attributes['skill-name'];
  if (attr) return attr;
  const data = item.data as { skill_name?: unknown; args?: { skill?: unknown } } | undefined;
  if (data && typeof data.skill_name === 'string' && data.skill_name) return data.skill_name;
  const skill = data?.args?.skill;
  return typeof skill === 'string' && skill ? skill : null;
}

/**
 * Pull the tool_use_id off either side of a TOOL_CALL/TOOL_RESULT pair.
 * Claude's emitter stores the id at `data.tool_call_id` for both calls and
 * results, but TOOL_RESULT also exposes it in `attributes['tool-use-id']`.
 * Read both so a missing field on one side doesn't break correlation.
 */
export function getToolUseId(item: FlowData): string | null {
  const data = item.data as { tool_call_id?: unknown } | undefined;
  if (data && typeof data.tool_call_id === 'string' && data.tool_call_id) {
    return data.tool_call_id;
  }
  const attr = item.attributes['tool-use-id'];
  return typeof attr === 'string' && attr ? attr : null;
}

/**
 * Pair every TOOL_CALL with the first TOOL_RESULT carrying the same
 * tool_use_id (within a single dense group). A call without a matching result
 * is "in flight"; a result without a call is rendered standalone.
 */
export interface ToolPair {
  call: FlowData;
  result: FlowData | null;
  /** The turn ended explicitly (for example, turn_aborted) without a result. */
  terminated: boolean;
}

export function pairToolEvents(events: FlowData[]): {
  pairs: ToolPair[];
  others: FlowData[];
  orphanResults: FlowData[];
  /** How many entries the turn renders as: one per pair, plus non-tool events
   *  and orphan results. The single source of truth for the "N events this
   *  turn" count (the dense-row summary and the footer chip both read it). */
  total: number;
} {
  const pairs: ToolPair[] = [];
  const others: FlowData[] = [];
  const orphanResults: FlowData[] = [];
  const callIndexById = new Map<string, number>();

  for (const item of events) {
    const t = item.elementType;
    if (t === FlowElementTypes.TOOL_CALL) {
      const id = getToolUseId(item);
      const pairIndex = pairs.push({ call: item, result: null, terminated: false }) - 1;
      if (id) callIndexById.set(id, pairIndex);
    } else if (t === FlowElementTypes.TOOL_RESULT) {
      const id = getToolUseId(item);
      const ix = id ? callIndexById.get(id) : undefined;
      if (ix !== undefined && pairs[ix].result === null) {
        pairs[ix].result = item;
      } else {
        orphanResults.push(item);
      }
    } else {
      others.push(item);
    }
  }

  // A turn is terminated when the backend says so semantically
  // (`turn-terminated: "true"`, stamped on flowpad cancel markers AND on
  // replayed vendor abort events), with the raw codex event subtype kept as a
  // fallback for live/PTY streams that bypass the replay stamping path.
  const turnTerminated = others.some(
    (item) =>
      item.elementType === FlowElementTypes.STATUS &&
      (item.attributes['turn-terminated'] === 'true' ||
        item.attributes.subtype === 'event_msg.turn_aborted'),
  );
  if (turnTerminated) {
    for (const pair of pairs) {
      if (pair.result === null) pair.terminated = true;
    }
  }

  return { pairs, others, orphanResults, total: pairs.length + others.length + orphanResults.length };
}
